import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { LockKey, ShieldCheck } from "@phosphor-icons/react";
import { validatePasswordPair, PASSWORD_RULE_TEXT } from "../lib/passwordPolicy";
import { PasswordInput } from "../components/PasswordInput";

export default function AccountSecurity() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  // Riders are passwordless — no password controls, setup status, forgot/reset, or invites.
  const isRider = profile?.role === "RID";

  const onSubmit = async (e) => {
    e.preventDefault();
    const perr = validatePasswordPair(pw, pw2);
    if (perr) { toast.error(perr); return; }
    setBusy(true);
    try {
      // Server-coordinated: reauth + password mutation + PASSWORD_CHANGED audit + global session
      // revocation (incl. this session) are reliably coupled on the backend — no best-effort ping.
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      try { await supabase.auth.signOut(); } catch (_) { /* session already revoked server-side */ }
      toast.success("Password changed — please sign in again");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err?.displayMessage || "We couldn't change your password. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto p-6" data-testid="account-security-page">
      <div className="mb-4 text-xs"><a href="/app" className="underline">← Back</a></div>
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={22} weight="fill" />
        <h1 className="heading text-2xl">Account security</h1>
      </div>
      <div className="overline mb-6">CHANGE YOUR PASSWORD</div>

      {isRider ? (
        <section className="grid-card grid-card-strong p-5" data-testid="rider-no-password-notice">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={18} weight="fill" />
            <span className="font-semibold text-sm">Passwordless account</span>
          </div>
          <p className="text-sm text-[hsl(var(--muted))]">
            You sign in as a Rider using a one-time code sent to <span className="font-semibold">{profile?.email}</span>.
            There is no password to manage.
          </p>
        </section>
      ) : (
      <section className="grid-card grid-card-strong p-5">
        <div className="text-xs text-[hsl(var(--muted))] mb-3">
          Signed in as <span className="font-semibold">{profile?.email}</span>
        </div>
        <form onSubmit={onSubmit} className="space-y-3" data-testid="change-password-form">
          <label className="block">
            <div className="overline mb-1.5">CURRENT PASSWORD</div>
            <PasswordInput value={cur} required autoComplete="current-password"
                   onChange={(e) => setCur(e.target.value)} data-testid="current-password-input" />
          </label>
          <label className="block">
            <div className="overline mb-1.5">NEW PASSWORD</div>
            <PasswordInput value={pw} required minLength={8} autoComplete="new-password"
                   onChange={(e) => setPw(e.target.value)} data-testid="new-password-input" />
          </label>
          <label className="block">
            <div className="overline mb-1.5">CONFIRM NEW PASSWORD</div>
            <PasswordInput value={pw2} required minLength={8} autoComplete="new-password"
                   onChange={(e) => setPw2(e.target.value)} data-testid="confirm-new-password-input" />
          </label>
          <p className="text-[10px] text-[hsl(var(--muted))]">{PASSWORD_RULE_TEXT} All your sessions (including this one) will be signed out.</p>
          <button type="submit" className="btn-primary w-full justify-center mt-2" disabled={busy || !cur || !pw || !pw2}
                  data-testid="change-password-submit">
            <LockKey size={14} weight="bold" /> Change password
          </button>
        </form>
      </section>
      )}
    </div>
  );
}
